import {
  CellValueChangedEvent,
  IServerSideDatasource,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { get } from "react-hook-form";
import { MaybernTableGetErrorForCellFn } from "shared/components/table/maybern/MaybernTableErrors";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";
import { useArray } from "shared/hooks/useArrayState";

interface MaybernTableBulkEditControllerProps {
  getSubmittedFieldValue: (rowIndex: number, field: string) => unknown;
  getFieldError: (rowIndex: number, field: string) => string | undefined;
}

export const useMaybernTableBulkEditController = <T extends object>({
  getSubmittedFieldValue,
  getFieldError,
}: MaybernTableBulkEditControllerProps) => {
  const [rowData, setRowData] = useState<T[]>(() => []);

  const cleanedRowData = useMemo(() => cleanRows(rowData), [rowData]);

  const { updateAt: updateRowAt } = useArray(rowData, setRowData);

  const gridRef = useRef<AgGridReact<T>>(null);

  const onClickClear = useCallback(() => {
    setRowData([]);
    tryGetAgGridApi(gridRef.current)?.refreshServerSide();
  }, []);

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<T>) => {
      updateRowAt(event.node.rowIndex!, (row) => ({
        ...row,
        [event.colDef.field!]: event.newValue,
      }));
    },
    [updateRowAt],
  );

  const rowDataRef = useRef(rowData);
  rowDataRef.current = rowData;

  const datasource = useMemo<IServerSideDatasource>(
    () => ({
      getRows: ({ request: { startRow = 0, endRow = 0 }, success }) => {
        success({
          rowData: new Array(endRow - startRow)
            .fill(null)
            .map((_, i) => rowDataRef.current[i + startRow] ?? {}),
        });
      },
    }),
    [],
  );

  const getErrorForCell = useCallback<MaybernTableGetErrorForCellFn<T>>(
    (params) => {
      const field = params.column!.getColDef().field!;
      const rowIndex = params.node.rowIndex!;
      const currentValue = get(rowDataRef.current, `${rowIndex}.${field}`);
      const submittedValue = getSubmittedFieldValue(rowIndex, field);
      if (currentValue !== submittedValue) {
        return undefined;
      }
      return getFieldError(rowIndex, field);
    },
    [getFieldError, getSubmittedFieldValue],
  );

  useEffect(() => {
    void cleanedRowData;
    void getFieldError;
    tryGetAgGridApi(gridRef.current)?.redrawRows();
  }, [cleanedRowData, getFieldError]);

  return {
    gridRef,
    datasource,
    onClickClear,
    onCellValueChanged,
    getErrorForCell,
    rowData: cleanedRowData,
  };
};

const cleanRows = <T extends object>(rows: T[]) => {
  const cleanedRows = rows.map((row) =>
    Object.values(row).some((value) => value) ? row : null,
  );
  const isAllRowsUndefined = cleanedRows.every((row) => !row);
  return isAllRowsUndefined ? [] : cleanedRows;
};
